import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch, RootState } from "../store";
import { invoiceActions } from "../slices/invoice";
import { InvoiceMapper } from "../../mappers/InvoiceMapper";
import { getErrorMessage } from "../../utils/errorUtils";

import { InvoiceRequestDto } from "../../shared/dtos/invoice/InvoiceRequestDto";
import { InvoiceResponseDto } from "../../shared/dtos/invoice/InvoiceResponseDto";
import { InvoiceSummaryDto } from "../../shared/dtos/invoice/InvoiceSummaryDto";
import { DateRangePaginationQueryDto } from "../../shared/dtos/common/DateRangePaginationQueryDto";
import { PaymentRequestDto } from "../../shared/dtos/payment/PaymentRequestDto";
import { EmailRequestDto } from "../../shared/dtos/common/EmailRequestDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";
import { SaleType } from "../../shared/enums/SaleType";

export interface ProductStub {
  id: string;
  name: string;
  wholesalePrice: number;
  retailPrice: number;
  brandName?: string;
  categoryName?: string;
}

export const getInvoices =
  (params?: DateRangePaginationQueryDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(invoiceActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<InvoiceSummaryDto>>
      >("/invoices", {
        headers: { requiresAuth: true },
        params,
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(invoiceActions.setItems(data.data.content));
        dispatch(invoiceActions.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(invoiceActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getInvoiceById =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(invoiceActions.setLoadingFetchOne());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<InvoiceResponseDto>
      >(`/invoices/${id}`, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(invoiceActions.setItem(data.data));
      }
    } catch (error) {
      dispatch(invoiceActions.setErrorFetchOne(getErrorMessage(error)));
    }
  };

export const emailInvoice =
  (id: string, emailPayload: EmailRequestDto) =>
  async (dispatch: AppDispatch): Promise<boolean> => {
    dispatch(invoiceActions.setLoadingEmail());
    try {
      const { data } = await axiosInstance.post<ApiResponseDto<void>>(
        `/invoices/email/${id}`,
        emailPayload,
        { headers: { requiresAuth: true } },
      );
      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Invoice emailed successfully!");
        dispatch(invoiceActions.setEmailSuccess());
        return true;
      }
      return false;
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(invoiceActions.setErrorEmail(message));
      return false;
    }
  };

export const getInvoicePdf =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(invoiceActions.setLoadingPdf());
    try {
      const { data } = await axiosInstance.get<Blob>(`/invoices/pdf/${id}`, {
        headers: { requiresAuth: true },
        responseType: "blob",
      });

      const pdfBlobUrl = window.URL.createObjectURL(
        new Blob([data], { type: "application/pdf" }),
      );
      dispatch(invoiceActions.setPdfUrl(pdfBlobUrl));
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error("Failed to load PDF preview: " + message);
      dispatch(invoiceActions.setErrorPdf(message));
    }
  };

export const createInvoice =
  (payload: InvoiceRequestDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(invoiceActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<InvoiceResponseDto>
      >("/invoices", payload, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Invoice created successfully!");
        dispatch(invoiceActions.clearInvCart());
        dispatch(
          invoiceActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );

        const summary = InvoiceMapper.mapInvoiceToSummary(data.data);
        if (summary) dispatch(invoiceActions.createSuccess(summary));
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(invoiceActions.setErrorCreate(message));
    }
  };

export const recordInvoicePayment =
  (id: string, payload: PaymentRequestDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(invoiceActions.setLoadingPayment());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<InvoiceResponseDto>
      >(`/invoices/payments/${id}`, payload, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Payment recorded!");
        dispatch(invoiceActions.paymentSuccess(data.data));
        dispatch(
          invoiceActions.setServerStatus({
            operation: "pay",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(invoiceActions.setErrorPayment(message));
    }
  };

export const voidInvoice =
  (id: string, reason: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(invoiceActions.setLoadingVoid());
    try {
      const { data } = await axiosInstance.post<ApiResponseDto<void>>(
        `/invoices/void/${id}?reason=${encodeURIComponent(reason)}`,
        null,
        { headers: { requiresAuth: true } },
      );
      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Invoice voided!");
        dispatch(
          invoiceActions.setServerStatus({
            operation: "void",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(invoiceActions.setErrorVoid(message));
    }
  };

export const resetInvoiceServerStatus =
  () =>
  (dispatch: AppDispatch): void => {
    dispatch(invoiceActions.setServerStatus(null));
  };

// --- Cart Actions ---
export const addInvItem =
  (product: ProductStub) =>
  (dispatch: AppDispatch, getState: () => RootState): void => {
    const { saleType } = getState().invoice;
    const price =
      saleType === SaleType.WHOLESALE
        ? product.wholesalePrice
        : product.retailPrice;
    dispatch(
      invoiceActions.invItemAdd({
        itemId: product.id,
        name: product.name,
        brand: product.brandName,
        category: product.categoryName,
        price: price,
        discount: 0,
        qty: 1,
      }),
    );
  };

export const changeInvoiceSaleType =
  (newType: SaleType) =>
  (dispatch: AppDispatch, getState: () => RootState): void => {
    const { cartItems } = getState().invoice;
    if (cartItems.length > 0) {
      toast.error(
        "Cannot switch sale types with items in the cart. Please clear the items first.",
      );
      return;
    }
    dispatch(invoiceActions.setInvSaleType(newType));
  };

export const updateInvItemFields =
  (itemId: string, updates: any) =>
  (dispatch: AppDispatch): void => {
    dispatch(invoiceActions.invItemUpdateFields({ itemId, updates }));
  };

export const invItemRemove = (itemId: string) => (dispatch: AppDispatch) =>
  dispatch(invoiceActions.invItemRemove(itemId));
export const setInvCustomer = (customer: any) => (dispatch: AppDispatch) =>
  dispatch(invoiceActions.setInvCustomer(customer));
export const clearInvCart = () => (dispatch: AppDispatch) =>
  dispatch(invoiceActions.clearInvCart());
export const removeInvItem = (itemId: string) => (dispatch: AppDispatch) =>
  dispatch(invoiceActions.invItemRemove(itemId));
